import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Panel, TextField, PrimaryButton, DefaultButton, PanelType } from '@fluentui/react';
import { RatingQuestion } from './RatingQuestion';
import { FeedbackData, setUserFeedback } from "../../api"

import styles from './FeedbackForm.module.css';

import { getToken } from '../../authConfig';
import { handleError } from '../../toaster';

interface Props {
  isOpen: boolean;
  onDismiss: () => void;
}

export const FeedbackForm = ({ isOpen, onDismiss }: Props) => {
  const client = useMsal().instance;
  const [feedbackComments, setFeedbackComments] = useState<string>('');
  const [ratings, setRatings] = useState({
    responseSatisfaction: 0,
    helpfulness: 0,
    easeOfUse: 0,
    reuseLikelihood: 0,
    overallExperience: 0,
  });

  const handleRatingChange = (key: keyof typeof ratings) => (value: number) => {
    setRatings(prevRatings => ({ ...prevRatings, [key]: value }));
  };

  const handleSubmit = async () => {
    try {
      const feedbackData: FeedbackData = {
        ratings: ratings,
        comments: feedbackComments,
      };
      const token = client ? await getToken(client) : undefined;
      await setUserFeedback(token, feedbackData);
    }
    catch (error) {
      handleError(error, "Error saving feedback");
    }
    finally {
      onDismiss();
    }
  };

  const isSubmitDisabled = Object.values(ratings).some(rating => rating === 0);

  return (
    <Panel
      type={PanelType.medium}
      headerText="Feedback"
      isOpen={isOpen}
      isBlocking={true}
      onDismiss={onDismiss}
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={() =>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryButton
            className={styles.panelButton}
            iconProps={{ iconName: 'Save' }}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            text="Submit"
          />
          <DefaultButton
            className={styles.panelButton}
            iconProps={{ iconName: 'Cancel' }}
            onClick={onDismiss}
            text="Close"
          />
        </div>
      }>
      <div style={{ padding: '20px' }}>
        <RatingQuestion
          label="How satisfied are you with the responses from Coeus?"
          ratingValue={ratings.responseSatisfaction}
          onRatingChange={handleRatingChange('responseSatisfaction')}
        />
        <RatingQuestion
          label="How helpful was the information provided by Coeus?"
          ratingValue={ratings.helpfulness}
          onRatingChange={handleRatingChange('helpfulness')}
        />
        <RatingQuestion
          label="How easy was it to interact with Coeus?"
          ratingValue={ratings.easeOfUse}
          onRatingChange={handleRatingChange('easeOfUse')}
        />
        <RatingQuestion
          label="How likely are you to use Coeus again?"
          ratingValue={ratings.reuseLikelihood}
          onRatingChange={handleRatingChange('reuseLikelihood')}
        />
        <RatingQuestion
          label="Overall, how would you rate your experience with Coeus?"
          ratingValue={ratings.overallExperience}
          onRatingChange={handleRatingChange('overallExperience')}
        />
        <TextField
          label="Comments (optional)"
          multiline
          rows={5}
          value={feedbackComments}
          onChange={(e, newValue) => setFeedbackComments(newValue || '')}
        />
      </div>
    </Panel>
  );
};